import { parseLocalDateYmd } from './utils/dateFormat';

/**
 * Período de experiência (45 / 90 dias) derivado apenas da data de admissão.
 * - Até 45 dias corridos após a admissão: "45"
 * - Do 46º ao 90º dia: "90"
 * - Fora desse intervalo ou sem data: null
 */
function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function getProbationPeriodFromHiring(
  hiringDate: string | undefined,
  referenceDate: Date = new Date()
): '45' | '90' | null {
  const diffDays = getDaysSinceHiring(hiringDate, referenceDate);
  if (diffDays === null) return null;
  if (diffDays <= 45) return '45';
  if (diffDays <= 90) return '90';
  return null;
}

/**
 * Dias corridos desde a admissão (0 no dia da admissão), ou null se data inválida/futura.
 */
export function getDaysSinceHiring(
  hiringDate: string | undefined,
  referenceDate: Date = new Date()
): number | null {
  const admission = parseLocalDateYmd(hiringDate);
  if (!admission) return null;

  const today = startOfLocalDay(referenceDate);
  const diffMs = today.getTime() - admission.getTime();
  if (diffMs < 0) return null;

  return Math.floor(diffMs / 86400000);
}

/** Cada fase de experiência tem 45 dias corridos. */
export const PROBATION_PHASE_DAYS = 45;

export interface ProbationCountdown {
  /** Fase atual: primeiros 45 dias ou do 46º ao 90º. */
  period: '45' | '90';
  /** Dias restantes até o fim da fase atual (0 no último dia). */
  daysRemaining: number;
  /** Duração da fase em dias (sempre 45). */
  periodTotalDays: number;
  /** 0–100: proporção do tempo restante na fase (barra regressiva). */
  remainingPercent: number;
}

/**
 * Calcula contagem regressiva da experiência para UI (barra e círculo).
 * Retorna null se o colaborador não estiver em período de experiência.
 */
export function getProbationCountdown(
  hiringDate: string | undefined,
  referenceDate: Date = new Date()
): ProbationCountdown | null {
  const period = getProbationPeriodFromHiring(hiringDate, referenceDate);
  const diffDays = getDaysSinceHiring(hiringDate, referenceDate);
  if (!period || diffDays === null) return null;

  const phaseEndDay = period === '45' ? PROBATION_PHASE_DAYS : 90;
  const daysRemaining = Math.max(0, phaseEndDay - diffDays);
  const remainingPercent = Math.round((daysRemaining / PROBATION_PHASE_DAYS) * 100);

  return {
    period,
    daysRemaining,
    periodTotalDays: PROBATION_PHASE_DAYS,
    remainingPercent,
  };
}

/**
 * Data de término da fase atual de experiência (YYYY-MM-DD).
 * Retorna null se o colaborador não estiver em período de experiência.
 */
export function getProbationPhaseEndDate(
  hiringDate: string | undefined,
  referenceDate: Date = new Date(),
): string | null {
  const period = getProbationPeriodFromHiring(hiringDate, referenceDate);
  const admission = parseLocalDateYmd(hiringDate);
  if (!period || !admission) return null;

  const phaseEndDay = period === '45' ? PROBATION_PHASE_DAYS : 90;
  const end = new Date(admission.getFullYear(), admission.getMonth(), admission.getDate() + phaseEndDay);
  const y = end.getFullYear();
  const m = String(end.getMonth() + 1).padStart(2, '0');
  const d = String(end.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
